import { useCallback, useEffect, useState } from 'react';
import { Car, Info, Plus } from 'lucide-react';
import type { Me } from '../api/auth';
import { transportApi } from '../api/transport';
import type { Attestation, MyVehicles, Vehicle } from '../api/transport';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { TextField } from '../ui/Field';

/**
 * Your cars, and your own statement that you are fit to drive one.
 *
 * **Not under a unit, deliberately.** A car and a licence follow their owner, so a parent
 * dual-hatted across a troop and a pack enters one car rather than two — the same argument the
 * calendar feed rests on, and the same one that makes a Scout's own packing list global. It is also
 * what makes this reachable by a guardian who holds no membership anywhere and is very often the
 * person driving.
 *
 * **Everything here is yours.** No leader can add a car for you or confirm your licence on your
 * behalf: a leader guessing at a parent's seat count is precisely the number nobody should be able
 * to enter, and a statement about your own age and licence is not one another person can make for
 * you.
 *
 * **The product never says "verified".** What it holds is what you told it, with the date you told
 * it — and the screen says so in those words, because displaying a verification nobody performed is
 * worse than displaying nothing: a leader stops asking.
 */
export function DrivingScreen({ me, onNavigate }: { me: Me; onNavigate: (path: string) => void }) {
  const [mine, setMine] = useState<MyVehicles | null>(null);
  const [attestation, setAttestation] = useState<Attestation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [vehicles, driver] = await Promise.all([
        transportApi.myVehicles(),
        transportApi.myAttestation(),
      ]);
      setMine(vehicles);
      setAttestation(driver);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your driving details.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  };

  const live = mine?.vehicles.filter((v) => !v.retiredAt) ?? [];
  const retired = mine?.vehicles.filter((v) => v.retiredAt) ?? [];

  return (
    <Page>
      <AppHeader me={me} onNavigate={onNavigate} />

      <section className="flex-1" style={{ background: 'var(--color-surface-page)' }}>
        <Gutter>
          <div className="flex flex-col gap-6" style={{ paddingBlock: 'var(--space-6)' }}>
            <header className="flex flex-col gap-2">
              <h1 className="t-headline flex items-center gap-2">
                <Car size={22} aria-hidden />
                Driving
              </h1>
              <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
                Your cars and your licence confirmation, kept in one place so you enter them once for
                every unit you drive for.
              </p>
            </header>

            {error && (
              <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)' }}>
                {error}
              </p>
            )}

            {attestation && (
              <AttestationCard
                attestation={attestation}
                busy={busy}
                onAttest={() => act(() => transportApi.attest(attestation.wordingVersion))}
              />
            )}

            <section className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="t-subheading">Your cars</h2>
                {!adding && (
                  <Button variant="secondary" onClick={() => setAdding(true)}>
                    <Plus size={16} aria-hidden />
                    Add a car
                  </Button>
                )}
              </div>

              {adding && mine && (
                <VehicleForm
                  capacityHelp={mine.capacityHelp}
                  minimum={mine.minimumSeatingCapacity}
                  maximum={mine.maximumSeatingCapacity}
                  busy={busy}
                  onCancel={() => setAdding(false)}
                  onSubmit={async (body) => {
                    await act(() => transportApi.addVehicle(body));
                    setAdding(false);
                  }}
                />
              )}

              {live.length === 0 && !adding && (
                <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
                  No cars yet. Add one and you can offer seats on any trip your unit is running.
                </p>
              )}

              {live.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  capacityHelp={mine?.capacityHelp ?? ''}
                  minimum={mine?.minimumSeatingCapacity ?? 2}
                  maximum={mine?.maximumSeatingCapacity ?? 15}
                  busy={busy}
                  act={act}
                />
              ))}

              {/*
                Retired cars stay. Never a delete: last year's trip still names the car it named,
                and a leader looking back at who drove needs the row to still be there.
              */}
              {retired.length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="t-label" style={{ color: 'var(--color-text-muted)' }}>
                    No longer driving
                  </h3>
                  {retired.map((v) => (
                    <p key={v.id} className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                      {v.description}
                    </p>
                  ))}
                </div>
              )}
            </section>
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

/**
 * The claim, and the fact that it is a claim.
 *
 * Age is attested rather than computed, and the reason is worth knowing: the product holds a date of
 * birth for youth and never asks adults for one, so there is nothing to compute from — and a screen
 * that inferred "adult, therefore over 21" would be guessing on the one question where guessing puts
 * a child in a car with somebody nobody checked.
 */
function AttestationCard({
  attestation,
  busy,
  onAttest,
}: {
  attestation: Attestation;
  busy: boolean;
  onAttest: () => Promise<void>;
}) {
  const expires = attestation.expiresAt ? new Date(attestation.expiresAt) : null;
  const soon = expires ? expires.getTime() - Date.now() < 30 * 24 * 3600 * 1000 : false;

  return (
    <section
      className="flex flex-col gap-3 p-4"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
      }}
    >
      <h2 className="t-subheading">Before you can be offered as a driver</h2>

      <p className="t-body">{attestation.wording}</p>

      {attestation.attested ? (
        <>
          <p className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            {/* The words for what actually happened. Never "verified" -- nobody checked anything. */}
            You confirmed this on{' '}
            {attestation.attestedAt && new Date(attestation.attestedAt).toLocaleDateString()}
            {expires && `, and it lasts until ${expires.toLocaleDateString()}`}.
          </p>

          {soon && (
            <p className="t-caption flex items-start gap-2" style={{ color: 'var(--color-text-secondary)' }}>
              <Info size={14} aria-hidden style={{ marginTop: '2px', flexShrink: 0 }} />
              That runs out soon. Confirming again puts it back to a full year.
            </p>
          )}

          <Button variant="secondary" onClick={onAttest} disabledReason={busy ? 'Saving…' : undefined}>
            Confirm again
          </Button>
        </>
      ) : (
        <>
          <p className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            Units re-collect this once a year, the same as they do on paper. Until you confirm it, you
            will not appear as a driver anywhere.
          </p>
          <Button onClick={onAttest} disabledReason={busy ? 'Saving…' : undefined}>
            I confirm this
          </Button>
        </>
      )}
    </section>
  );
}

function VehicleCard({
  vehicle,
  capacityHelp,
  minimum,
  maximum,
  busy,
  act,
}: {
  vehicle: Vehicle;
  capacityHelp: string;
  minimum: number;
  maximum: number;
  busy: boolean;
  act: (work: () => Promise<unknown>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <VehicleForm
        initial={vehicle}
        capacityHelp={capacityHelp}
        minimum={minimum}
        maximum={maximum}
        busy={busy}
        onCancel={() => setEditing(false)}
        onSubmit={async (body) => {
          await act(async () => {
            const { offersLowered } = await transportApi.updateVehicle(vehicle.id, body);

            // Never silent. Correcting a six-seat car to four also changed what some event's seat
            // count is relying on, and you are the only person who can tell the coordinator.
            if (offersLowered > 0) {
              window.alert(
                `${offersLowered} offer${offersLowered === 1 ? '' : 's'} you had made ` +
                  'came down to match.',
              );
            }
          });
          setEditing(false);
        }}
      />
    );
  }

  return (
    <article
      className="flex flex-wrap items-center justify-between gap-3 p-4"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
      }}
    >
      <div className="flex flex-col gap-1">
        <span className="t-body-strong">{vehicle.description}</span>
        <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
          {/* Including the driver's, per TROOP-136 — so the caption says so rather than leaving
              the reader to remember which convention this number is in. */}
          {vehicle.seatingCapacity} belted seats including you
          {vehicle.licensePlate && ` · ${vehicle.licensePlate}`}
        </span>
      </div>

      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => setEditing(true)}>
          Change
        </Button>
        <Button
          variant="secondary"
          onClick={() => act(() => transportApi.retireVehicle(vehicle.id))}
          disabledReason={busy ? 'Saving…' : undefined}
        >
          No longer driving this
        </Button>
      </div>
    </article>
  );
}

function VehicleForm({
  initial,
  capacityHelp,
  minimum,
  maximum,
  busy,
  onCancel,
  onSubmit,
}: {
  initial?: Vehicle;
  /** From the server, so the definition of a seat is written once rather than twice. */
  capacityHelp: string;
  /** Both bounds come from the server too, for the same reason. */
  minimum: number;
  maximum: number;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: {
    description: string;
    colour: string | null;
    seatingCapacity: number;
    licensePlate: string | null;
  }) => Promise<void>;
}) {
  const [description, setDescription] = useState(initial?.description ?? '');
  const [colour, setColour] = useState(initial?.colour ?? '');
  const [seats, setSeats] = useState(String(initial?.seatingCapacity ?? 5));
  const [plate, setPlate] = useState(initial?.licensePlate ?? '');

  return (
    <form
      className="flex flex-col gap-3 p-4"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
      }}
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit({
          description: description.trim(),
          colour: colour.trim() || null,
          seatingCapacity: Number(seats),
          licensePlate: plate.trim() || null,
        });
      }}
    >
      <TextField
        label="The car"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        required
        hint="Enough that a family spots it in a car park — “blue Honda Odyssey”."
      />

      <TextField label="Colour" value={colour} onChange={(e) => setColour(e.target.value)} />

      <TextField
        label="Seats in the car, including you"
        type="number"
        min={minimum}
        max={maximum}
        value={seats}
        onChange={(e) => setSeats(e.target.value)}
        required
        hint={capacityHelp}
      />

      <TextField
        label="Number plate"
        value={plate}
        onChange={(e) => setPlate(e.target.value)}
        hint="Optional, and only ever shown to people on a trip you have offered to drive."
      />

      <div className="flex gap-2">
        <Button type="submit" disabledReason={busy ? 'Saving…' : undefined}>
          {initial ? 'Save this car' : 'Add this car'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
